import React, { useState } from 'react';
import styled from 'styled-components';
import { Upload, message, Modal, Image } from 'antd';
import { UploadOutlined, CloseOutlined } from '@ant-design/icons';
import type { UploadProps } from 'antd';
import BaseImageIcon from './BaseImage.svg';
import { API_URL } from '../../../../helpers/constants';

interface FileUploadProps {
  value?: string;
  onChange?: (value: string) => void;
  onUploadTypeChange?: (type: 'IMAGE' | 'VIDEO') => void;
}

const FileUpload: React.FC<FileUploadProps> = ({ value, onChange, onUploadTypeChange }) => {
  const [fileList, setFileList] = useState<any[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [imageError, setImageError] = useState(false);
  const [fileType, setFileType] = useState<'IMAGE' | 'VIDEO'>('IMAGE');
  const [isModalVisible, setIsModalVisible] = useState(false);

  const token = localStorage.getItem('token');

  const handleUploadChange: UploadProps['onChange'] = (info) => {
    const { file, fileList: newFileList } = info;
    // Only keep the latest file
    const latestFileList = newFileList.slice(-1);
    setFileList(latestFileList);

    if (file.status === 'uploading') {
      setUploading(true);
      setUploadProgress(file.percent || 0);

      // Detect file type when uploading starts
      if (file.type) {
        const uploadType = file.type.startsWith('video/') ? 'VIDEO' : 'IMAGE';
        setFileType(uploadType);
        onUploadTypeChange?.(uploadType);
      }
    }

    if (file.status === 'done') {
      setUploadProgress(100);
      setTimeout(() => {
        setUploading(false);
        setUploadProgress(0);
        if (file.response && file.response.data) {
          const uploadedUrl = file.response.data;
          setPreviewUrl(uploadedUrl);
          onChange?.(uploadedUrl);
          message.success(`${file.name} uploaded successfully!`);
        } else {
          message.error('Upload successful but no URL returned');
        }
      }, 200);
    } else if (file.status === 'error') {
      setUploading(false);
      setUploadProgress(0);
      message.error(`${file.name} file upload failed.`);
    }
  };

  const handleRemoveFile = () => {
    setFileList([]);
    setUploading(false);
    setUploadProgress(0);
    setPreviewUrl('');
    setImageError(false);
    setFileType('IMAGE');
    onChange?.('');
    onUploadTypeChange?.('IMAGE');
  };

  const handleLinkInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange?.(newValue);
  };

  const beforeUpload = (file: File) => {
    const isPNG = file.type === 'image/png';
    const isJPG = file.type === 'image/jpeg';
    const isVideo = file.type.startsWith('video/');

    if (!isPNG && !isJPG && !isVideo) {
      message.error('You can only upload PNG, JPG or video files!');
      return false;
    }

    const isLt50M = file.size / 1024 / 1024 < 50;
    if (!isLt50M) {
      message.error('File must be smaller than 50MB!');
      return false;
    }

    return true;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + sizes[i];
  };

  const uploadProps: UploadProps = {
    name: 'file',
    action: `${API_URL}/api/v1/admin/notifications/upload`,
    onChange: handleUploadChange,
    beforeUpload: beforeUpload,
    fileList: fileList,
    showUploadList: false,
    headers: { Authorization: `Bearer ${token}` },
  };

  const handleVideoClick = () => {
    if (fileType === 'VIDEO' && previewUrl) {
      setIsModalVisible(true);
    }
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
  };

  return (
    <ContainerStyled>
      <InputWrapperStyled>
        <StyledInput placeholder='Add link or upload file' value={value} onChange={handleLinkInputChange} />
        <Upload {...uploadProps}>
          <UploadButtonStyled>
            <UploadOutlined />
          </UploadButtonStyled>
        </Upload>
      </InputWrapperStyled>

      {fileList.length > 0 && (
        <FileItemStyled>
          <FileIconStyled onClick={handleVideoClick} isClickable={fileType === 'VIDEO' && !uploading && !!previewUrl}>
            {!uploading && previewUrl && !imageError ? (
              fileType === 'VIDEO' ? (
                <VideoPreviewThumbnail>
                  <video width='40' height='40'>
                    <source src={previewUrl} type='video/mp4' />
                  </video>
                  <PlayIconOverlay>▶</PlayIconOverlay>
                </VideoPreviewThumbnail>
              ) : (
                <Image src={previewUrl} alt={fileList[0]?.name} width={40} height={40} fallback={BaseImageIcon} />
              )
            ) : (
              <BaseImageStyled src={BaseImageIcon} alt='uploading' />
            )}
          </FileIconStyled>

          <FileContentStyled>
            <FileTopRowStyled>
              <FileNameStyled>{fileList[0].name}</FileNameStyled>
              <FileSizeStyled>{formatFileSize(fileList[0].size)}</FileSizeStyled>
            </FileTopRowStyled>

            {uploading && (
              <ProgressBarContainerStyled>
                <ProgressBarStyled progress={uploadProgress} />
              </ProgressBarContainerStyled>
            )}
          </FileContentStyled>

          {!uploading && (
            <RemoveButtonStyled onClick={handleRemoveFile}>
              <CloseOutlined />
            </RemoveButtonStyled>
          )}
        </FileItemStyled>
      )}

      <FormatsTextStyled>Formats accepted are png, jpg, mp4</FormatsTextStyled>

      <Modal title='Video Preview' open={isModalVisible} onCancel={handleCloseModal} footer={null} width={800} centered>
        <VideoModalContent>
          <video width='100%' controls autoPlay>
            <source src={previewUrl} type='video/mp4' />
            Your browser does not support the video tag.
          </video>
        </VideoModalContent>
      </Modal>
    </ContainerStyled>
  );
};

export default FileUpload;

const ContainerStyled = styled.div`
  width: 100%;
`;

const InputWrapperStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`;

const StyledInput = styled.input`
  flex: 1;
  height: 40px;
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  font-family: Poppins;
  font-size: 14px;
  color: #1d2129;
  outline: none;
  transition: all 0.3s;

  &:focus {
    border-color: #40a9ff;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }

  &::placeholder {
    color: #bfbfbf;
  }

  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
`;

const UploadButtonStyled = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: #1890ff;
  border: none;
  border-radius: 6px;
  color: white;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background: #40a9ff;
  }

  &:active {
    background: #096dd9;
  }
`;

const FileItemStyled = styled.div`
  display: flex;
  align-items: center;
  padding: 16px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  margin-bottom: 8px;
  gap: 16px;
`;

const FileContentStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  min-width: 0;
`;

const FileTopRowStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

const FileIconStyled = styled.div<{ isClickable?: boolean }>`
  width: 48px;
  height: 48px;
  min-width: 48px;
  background: #e6f7ff;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
  cursor: ${(props) => (props.isClickable ? 'pointer' : 'default')};
  transition: all 0.3s;

  &:hover {
    ${(props) =>
      props.isClickable &&
      `
      transform: scale(1.05);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    `}
  }
`;

const VideoPreviewThumbnail = styled.div`
  position: relative;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;

  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const PlayIconOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 20px;
  height: 20px;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 10px;
  padding-left: 2px;
`;

const BaseImageStyled = styled.img`
  width: 40px;
  height: 40px;
  object-fit: contain;
`;

const FilePreviewImage = styled.img`
  width: 40px;
  height: 40px;
  object-fit: cover;
`;

const VideoModalContent = styled.div`
  width: 100%;
  background: #000;
  border-radius: 8px;
  overflow: hidden;

  video {
    display: block;
    width: 100%;
    height: auto;
  }
`;

const FileNameStyled = styled.div`
  font-family: Poppins;
  font-size: 16px;
  font-weight: 600;
  color: #1d2129;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FileSizeStyled = styled.div`
  font-family: Poppins;
  font-size: 16px;
  font-weight: 400;
  color: #86909c;
  white-space: nowrap;
`;

const ProgressBarContainerStyled = styled.div`
  position: relative;
  width: 100%;
  height: 8px;
  background: #f0f0f0;
  border-radius: 4px;
  overflow: hidden;
`;

const ProgressBarStyled = styled.div<{ progress: number }>`
  width: ${(props) => props.progress}%;
  height: 100%;
  background: linear-gradient(90deg, #1890ff 0%, #40a9ff 100%);
  transition: width 0.3s ease;
  box-shadow: 0 0 10px rgba(24, 144, 255, 0.3);
  border-radius: 4px;
`;

const RemoveButtonStyled = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: transparent;
  border: none;
  color: #8c8c8c;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s;
  border-radius: 4px;

  &:hover {
    background: #ff4d4f;
    color: white;
  }
`;

const FormatsTextStyled = styled.div`
  font-family: Poppins;
  font-size: 12px;
  color: #ff4d4f;
  margin-top: 4px;
`;
